import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { PARKING_TYPES } from "@/lib/parkingTypes";

export default function EditSlotDialog({ open, onOpenChange, slot, onSaved }) {
    const [form, setForm] = useState({ parking_type: "covered", block: "", floor: "", notes: "", is_active: true });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open || !slot) return;
        setForm({
            parking_type: slot.parking_type || "covered", block: slot.block || "", floor: slot.floor || "",
            notes: slot.notes || "", is_active: slot.is_active !== false,
        });
    }, [open, slot]);

    function set(field, value) { setForm((f) => ({ ...f, [field]: value })); }

    async function handleSubmit(e) {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            await api.patch(`/parking/slots/${slot.id}`, {
                parking_type: form.parking_type, block: form.block || null, floor: form.floor || null,
                notes: form.notes || null, is_active: form.is_active,
            });
            toast.success(`Slot ${slot.slot_number} updated`);
            onOpenChange(false);
            onSaved();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSubmitting(false);
        }
    }

    if (!slot) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">Edit slot {slot.slot_number}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label>Parking type</Label>
                        <Select value={form.parking_type} onValueChange={(v) => set("parking_type", v)}>
                            <SelectTrigger data-testid="edit-slot-parking-type-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                            <SelectContent>{PARKING_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Block</Label>
                            <Input data-testid="edit-slot-block-input" value={form.block} onChange={(e) => set("block", e.target.value)} className="rounded-sm border-brand-line" />
                        </div>
                        <div className="space-y-2">
                            <Label>Floor</Label>
                            <Input data-testid="edit-slot-floor-input" value={form.floor} onChange={(e) => set("floor", e.target.value)} className="rounded-sm border-brand-line" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Notes</Label>
                        <Textarea data-testid="edit-slot-notes-input" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-sm border-brand-line" />
                    </div>
                    <div className="flex items-center justify-between border-t border-brand-line pt-4">
                        <div>
                            <Label className="text-brand-ink">Active</Label>
                            <p className="text-xs text-brand-inkSoft">Inactive slots are excluded from allocation and billing.</p>
                        </div>
                        <Switch data-testid="edit-slot-active-switch" checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="edit-slot-cancel-btn">Cancel</Button>
                        <Button type="submit" disabled={submitting} data-testid="edit-slot-submit-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                            {submitting ? "Saving…" : "Save changes"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
